/**
 * Work Orders API service
 * Handles all work order-related API operations
 */

import { httpClient } from '@/utils';
import type {
    APIWorkOrder,
    APIWorkOrderDetail,
    APIWorkOrderRequest,
    DRFPaginatedResponse,
    WorkOrderStatus,
} from '@/types';

/**
 * API endpoints for work orders
 */
const ENDPOINTS = {
    base: '/work-orders/',
    byId: (id: number) => `/work-orders/${id}/`,
} as const;

/**
 * Query parameters for listing work orders
 */
export interface WorkOrderListParams {
    page?: number;
    page_size?: number;
    status?: WorkOrderStatus;
    client?: number;
}

/**
 * Work orders service interface
 */
export interface WorkOrdersService {
    list(params?: WorkOrderListParams): Promise<DRFPaginatedResponse<APIWorkOrder>>;
    getById(id: number): Promise<APIWorkOrderDetail>;
    create(data: APIWorkOrderRequest): Promise<APIWorkOrder>;
    update(id: number, data: APIWorkOrderRequest): Promise<APIWorkOrder>;
    patch(id: number, data: Partial<APIWorkOrderRequest>): Promise<APIWorkOrder>;
    remove(id: number): Promise<void>;
}

/**
 * Work orders service implementation
 */
export const workOrdersService: WorkOrdersService = {
    /**
     * Get paginated list of work orders
     */
    async list(params?: WorkOrderListParams): Promise<DRFPaginatedResponse<APIWorkOrder>> {
        const searchParams = new URLSearchParams();

        if (params?.page !== undefined) {
            searchParams.set('page', String(params.page));
        }
        if (params?.page_size !== undefined) {
            searchParams.set('page_size', String(params.page_size));
        }
        if (params?.status !== undefined) {
            searchParams.set('status', params.status);
        }
        if (params?.client !== undefined) {
            searchParams.set('client', String(params.client));
        }

        const query = searchParams.toString();
        const endpoint = query ? `${ENDPOINTS.base}?${query}` : ENDPOINTS.base;

        return httpClient.get<DRFPaginatedResponse<APIWorkOrder>>(endpoint);
    },

    /**
     * Get single work order by ID (full detail)
     */
    async getById(id: number): Promise<APIWorkOrderDetail> {
        return httpClient.get<APIWorkOrderDetail>(ENDPOINTS.byId(id));
    },

    /**
     * Create a new work order
     */
    async create(data: APIWorkOrderRequest): Promise<APIWorkOrder> {
        return httpClient.post<APIWorkOrder, APIWorkOrderRequest>(ENDPOINTS.base, data, {
            successTitle: 'Orden creada',
            successMessage: 'La orden de trabajo se creó correctamente.',
            errorTitle: 'Error en orden de trabajo',
        });
    },

    /**
     * Full update of an existing work order
     */
    async update(id: number, data: APIWorkOrderRequest): Promise<APIWorkOrder> {
        return httpClient.put<APIWorkOrder, APIWorkOrderRequest>(ENDPOINTS.byId(id), data, {
            successTitle: 'Orden actualizada',
            successMessage: 'Los cambios de la orden de trabajo se guardaron.',
            errorTitle: 'Error en orden de trabajo',
        });
    },

    /**
     * Partial update of an existing work order
     */
    async patch(id: number, data: Partial<APIWorkOrderRequest>): Promise<APIWorkOrder> {
        return httpClient.patch<APIWorkOrder, Partial<APIWorkOrderRequest>>(
            ENDPOINTS.byId(id),
            data,
            {
                successTitle: 'Orden actualizada',
                successMessage: 'Se actualizó el estado de la orden de trabajo.',
                errorTitle: 'Error en orden de trabajo',
            }
        );
    },

    /**
     * Delete a work order
     */
    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(ENDPOINTS.byId(id), {
            successTitle: 'Orden eliminada',
            successMessage: 'La orden de trabajo se eliminó correctamente.',
            errorTitle: 'Error en orden de trabajo',
        });
    },
};

export default workOrdersService;
